import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiExternalLink, FiSearch } from 'react-icons/fi';
import { listFeedPosts } from '../../services/feedPostsApi';
import './NewsPage.css';

const EXIT_NEWS_MESSAGE =
  'Estás por salir de Flotte para consultar una noticia externa. ¿Deseas continuar?';

const normalizeCategory = value => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

const NEWS_CATEGORY_KEYS = new Set(['news', 'news_agent', 'news_agency']);

const isNewsPost = post => {
  if (!post) return false;
  if (post.isNews === true) return true;
  if (post.company?.isNews) return true;
  const pool = [
    post.visibility,
    post.companyCategory,
    post.companyType,
  ].filter(Boolean);
  if (Array.isArray(post.companyCategories)) {
    pool.push(...post.companyCategories);
  }
  return pool.some(value => NEWS_CATEGORY_KEYS.has(normalizeCategory(value)));
};

const normalizeUrl = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const extractHost = url => {
  if (!url) return '';
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

const formatRelativeDate = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelativeShort = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'Justo ahora';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Hace ${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Ayer';
  return `${diffDays} días`;
};

const resolveTimelineTone = iso => {
  if (!iso) return 'older';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'older';
  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 60) return 'now';
  if (diffMinutes < 24 * 60) return 'today';
  return 'older';
};

const resolveCardId = (item, index) =>
  item?.id || item?.created || item?.createdAt || `news-${index}`;

export default function NewsPage() {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [highlightId, setHighlightId] = useState('');
  const cardRefs = useRef(new Map());
  const highlightTimerRef = useRef(null);

  useEffect(() => {
    let ignore = false;
    const hydrateNews = async () => {
      setLoading(true);
      setError('');
      try {
        const items = await listFeedPosts({ limit: 120 });
        if (ignore) return;
        const news = items.filter(isNewsPost);
        setNewsItems(news);
      } catch (err) {
        if (!ignore) {
          setError(err?.message || 'No pudimos cargar las noticias.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    hydrateNews();
    return () => {
      ignore = true;
    };
  }, []);

  const orderedNews = useMemo(() => {
    const source = Array.isArray(newsItems) ? newsItems : [];
    return [...source].sort((a, b) => {
      const dateA = a?.created ? new Date(a.created).getTime() : 0;
      const dateB = b?.created ? new Date(b.created).getTime() : 0;
      return dateB - dateA;
    });
  }, [newsItems]);

  const filteredNews = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return orderedNews;
    return orderedNews.filter(item => {
      const haystack = [
        item.company?.name,
        item.content,
        item.newsLink,
        item.company?.category,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [orderedNews, query]);

  const last24HoursCount = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return orderedNews.filter(item => {
      const timestamp = item?.created ? new Date(item.created).getTime() : 0;
      return timestamp >= cutoff;
    }).length;
  }, [orderedNews]);

  const handleOpenCompany = useCallback(
    company => {
      if (!company) return;
      const slug = typeof company.slug === 'string' && company.slug.trim();
      const id = typeof company.id === 'string' && company.id.trim();
      const target = slug
        ? `/empresa/${encodeURIComponent(slug)}`
        : id
        ? `/empresa/${encodeURIComponent(id)}`
        : '';
      if (target) {
        navigate(target);
      }
    },
    [navigate]
  );

  const handleOpenExternal = useCallback(url => {
    const normalized = normalizeUrl(url);
    if (!normalized) return;
    const confirmed = window.confirm(EXIT_NEWS_MESSAGE);
    if (!confirmed) return;
    window.open(normalized, '_blank', 'noopener,noreferrer');
  }, []);

  const registerCardRef = useCallback((id, node) => {
    if (!cardRefs.current) {
      cardRefs.current = new Map();
    }
    if (node) {
      cardRefs.current.set(id, node);
    } else {
      cardRefs.current.delete(id);
    }
  }, []);

  const handleTimelineClick = useCallback(id => {
    const node = cardRefs.current?.get(id);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      setHighlightId(id);
      highlightTimerRef.current = setTimeout(() => setHighlightId(''), 1400);
    }
  }, []);

  useEffect(() => () => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
  }, []);

  const cards = useMemo(
    () =>
      filteredNews.map((item, index) => ({
        item,
        cardId: resolveCardId(item, index),
      })),
    [filteredNews]
  );

  const timelineEntries = cards.slice(0, 10);

  const accentClasses = ['news-card--accent-blue', 'news-card--accent-indigo', 'news-card--accent-teal'];

  const skeletonCards = useMemo(() => Array.from({ length: 4 }), []);
  const skeletonTimeline = useMemo(() => Array.from({ length: 5 }), []);

  return (
    <main className="news-page">
      <header className="news-page__header">
        <div className="news-page__header-row">
          <div className="news-page__heading">
            <h1>Noticias del ecosistema Flotte</h1>
            <span className="news-page__badge">{last24HoursCount} Nuevas</span>
          </div>
          <div className="news-page__toolbar">
            <label className="news-page__search">
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                placeholder="Buscar por empresa, palabra clave o enlace"
                value={query}
                onChange={event => setQuery(event.target.value)}
              />
            </label>
            <button type="button" className="news-page__back" onClick={() => navigate('/dashboard?section=feed')}>
              <FiArrowLeft aria-hidden="true" />
              Volver al feed
            </button>
          </div>
        </div>
      </header>

      <section className="news-page__layout">
        {loading ? (
          <>
            <div className="news-page__list news-page__list--loading" aria-hidden="true">
              {skeletonCards.map((_, index) => (
                <article key={`skeleton-card-${index}`} className="news-card news-card--skeleton">
                  <header className="news-card__header">
                    <span className="skeleton-line skeleton-line--xs skeleton-line--wide" />
                    <span className="skeleton-line skeleton-line--xs skeleton-line--short" />
                  </header>
                  <div className="skeleton-line skeleton-line--lg" />
                  <div className="skeleton-line skeleton-line--md" />
                  <div className="news-card__meta">
                    <span className="skeleton-pill" />
                  </div>
                  <div className="news-card__footer">
                    <span className="skeleton-line skeleton-line--xs skeleton-line--short" />
                    <span className="skeleton-line skeleton-line--xs skeleton-line--short" />
                  </div>
                </article>
              ))}
            </div>
            <aside className="news-timeline news-timeline--loading" aria-hidden="true">
              <div className="news-timeline__inner">
                <h2>Actividad reciente</h2>
                <div className="news-timeline__line">
                  <ul>
                    {skeletonTimeline.map((_, index) => (
                      <li key={`timeline-skeleton-${index}`} className="timeline-item timeline-item--skeleton">
                        <div className="timeline-item__skeleton">
                          <span className="skeleton-line skeleton-line--xs skeleton-line--short" />
                          <span className="skeleton-line skeleton-line--sm" />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </>
        ) : error ? (
          <div className="news-page__empty news-page__empty--error">
            <p>{error}</p>
            <p>Recarga la página para intentarlo nuevamente.</p>
          </div>
        ) : !filteredNews.length ? (
          <div className="news-page__empty">
            <p>No encontramos noticias con los filtros actuales.</p>
            <p>Intenta ajustar la búsqueda o vuelve más tarde.</p>
          </div>
        ) : (
          <>
            <div className="news-page__list">
              {cards.map(({ item, cardId }, index) => {
                const creationDate = item.created || item.createdAt || '';
                const timestamp = formatRelativeDate(creationDate);
                const content = item.content || '';
                const companyName = item.company?.name || 'Fuente privada';
                const link = normalizeUrl(item.newsLink || '');
                const host = extractHost(link);
                const accentClass = accentClasses[index % accentClasses.length];
                const isHighlighted = highlightId === cardId;
                const tagLabel = host || item.company?.category || 'Actualización';
                return (
                  <article
                    key={cardId}
                    ref={node => registerCardRef(cardId, node)}
                    className={`news-card ${accentClass}${isHighlighted ? ' news-card--highlight' : ''}`}
                    tabIndex={-1}
                  >
                    <header className="news-card__header">
                      <button
                        type="button"
                        className="news-card__source"
                        onClick={() => handleOpenCompany(item.company)}
                      >
                        {companyName}
                      </button>
                      {timestamp ? <time dateTime={creationDate}>{timestamp}</time> : null}
                    </header>
                    <p className="news-card__headline">{content}</p>
                    {tagLabel ? (
                      <div className="news-card__meta">
                        <span className="news-card__pill">{tagLabel}</span>
                      </div>
                    ) : null}
                    <div className="news-card__footer">
                      <button
                        type="button"
                        className="news-card__cta"
                        onClick={() => handleOpenCompany(item.company)}
                      >
                        <FiExternalLink aria-hidden="true" />
                        Perfil
                      </button>
                      {link ? (
                        <button
                          type="button"
                          className="news-card__readmore"
                          onClick={() => handleOpenExternal(link)}
                          aria-label="Leer la noticia externa"
                        >
                          Leer más
                          <FiArrowRight aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </article>
              );
              })}
            </div>
            <aside className="news-timeline">
              <div className="news-timeline__inner">
                <h2>Actividad reciente</h2>
                <div className="news-timeline__line">
                  <ul>
                    {timelineEntries.map(({ item, cardId }) => {
                      const creationDate = item.created || item.createdAt || '';
                      const relative = formatRelativeShort(creationDate);
                      const summary = item.content?.slice(0, 120) || 'Actualización publicada';
                      const tone = resolveTimelineTone(creationDate);
                      return (
                        <li key={cardId} className={`timeline-item timeline-item--${tone}`}>
                          <button
                            type="button"
                            onClick={() => handleTimelineClick(cardId)}
                          >
                            <span className="timeline-item__time">{relative}</span>
                            <p className="timeline-item__headline">{summary}</p>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </aside>
          </>
        )}
      </section>
    </main>
  );
}
